import React, { useState } from "react";
import { Button, ButtonProps } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { AnimatedImageStatus } from "@repo/api/animated-image";
import { useMemo } from "react";
import { DialogTrigger } from "@/lib/components/Dialog";
import { IconLoader2, IconShare2 } from "@tabler/icons-react";

export const ExportVideoButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { isRendering: boolean }
>(({ isRendering, ...props }, ref) => {
  // Using custom hover state to keep the tooltip open when the button is clicked.
  // The default tooltip behavior is to close when the button is clicked.
  const [isHovered, setIsHovered] = useState(false);

  const timelineItems = useEditorStore(
    (state) => state.video?.compositionJson.mainSceneTimeline,
  );
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const { tooltipText, isExportDisabled } = useMemo(() => {
    const animatedImageStatuses: AnimatedImageStatus[] = [];

    for (const timelineItem of timelineItems ?? []) {
      const animatedImage = timelineItem.animatedImageExternalId
        ? animatedImages[timelineItem.animatedImageExternalId]
        : undefined;

      if (animatedImage) {
        animatedImageStatuses.push(animatedImage.status);
      }
    }

    let buttonStatus: {
      tooltipText: string | null;
      isExportDisabled: boolean;
    } = {
      tooltipText: null,
      isExportDisabled: false,
    };

    if (
      animatedImageStatuses.length === 0 ||
      !animatedImageStatuses.some((status) => status === "COMPLETED")
    ) {
      buttonStatus = {
        tooltipText: "You need to animate at least one image before exporting",
        isExportDisabled: true,
      };
    }

    if (
      animatedImageStatuses.some((status) =>
        ["INITIAL", "PROCESSING", "POST_PROCESSING"].includes(status),
      )
    ) {
      buttonStatus.tooltipText =
        "Images are being animated, please wait a moment";
    }

    return buttonStatus;
  }, [timelineItems, animatedImages]);

  const button = (
    <DialogTrigger asChild>
      <Button
        disabled={isExportDisabled}
        size="sm"
        className="mr-4 [&_svg]:size-5"
        icon={
          isRendering ? (
            <IconLoader2 className="animate-spin" />
          ) : (
            <IconShare2 />
          )
        }
        iconPosition="leading"
        ref={ref}
        {...props}
      >
        Export video
      </Button>
    </DialogTrigger>
  );

  if (!tooltipText) {
    return button;
  }

  return (
    <Tooltip open={isHovered}>
      <TooltipTrigger
        asChild
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isExportDisabled ? (
          <span className="inline-flex">{button}</span>
        ) : (
          button
        )}
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
});
